/**
 * Table block (spec §7.4.16, §8.28): a grid of cells holding inline Markdown. A cell shows its Markdown
 * rendered; the focused cell mounts a single-line CodeMirror bound to its LoroText (imperative mount,
 * §8.6), so two people typing in one cell merge and a large table keeps one editor, not one per cell.
 * Tab/Shift+Tab and Enter move between cells (adding a row at the end), Alt+arrows move rows and
 * columns, Escape leaves; a pasted GFM table or tab-separated lines fill the grid from the cell.
 */
import { EditorState, Prec } from "@codemirror/state";
import { EditorView, keymap } from "@codemirror/view";
import type { LoroMap } from "loro-crdt";
import MarkdownIt from "markdown-it";
import { AlignCenter, AlignLeft, AlignRight, ArrowDownToLine, ArrowLeftToLine, ArrowRightToLine, ArrowUpToLine, ClipboardCopy, Columns2, Rows2 } from "lucide-react";
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { fromLoro, loroTextBinding } from "../../editors/loroText";
import { useT } from "../../i18n";
import type { DocHandle } from "../../sync/docManager";
import { useLoroSelector } from "../../sync/hooks";
import type { BlockComponentProps } from "../registry";
import { LoroTextInput, bodyMap } from "../textField";
import { parseGfm, parseTsv, toGfm } from "./gfm";
import { addColumn, addRow, cellText, deleteColumn, deleteRow, headerText, moveColumn, moveRow, pasteGrid, readTable, sameTable, setAlign, type Align, type TableState } from "./model";

/** A cell's place: `row` null is the header row. */
interface Pos {
  row: string | null;
  col: string;
}

const inline = new MarkdownIt("commonmark", { html: false, linkify: false }).enable("strikethrough");

const ALIGN_LABEL = { left: "table.align.left", center: "table.align.center", right: "table.align.right" } as const;

const samePos = (a: Pos | null, b: Pos | null) => !!a && !!b && a.row === b.row && a.col === b.col;

export function TableBlock({ handle, node, readOnly, onFocus }: BlockComponentProps) {
  const t = useT();
  const body = bodyMap(node);
  const table = useLoroSelector(handle.doc, () => (body ? readTable(body) : { caption: "", columns: [], rows: [] }), sameTable);
  const [active, setActive] = useState<Pos | null>(null);
  const [copied, setCopied] = useState(false);
  if (!body || !table) return null;

  const colIndex = (id: string) => table.columns.findIndex((c) => c.id === id);
  const rowIndex = (id: string | null) => (id === null ? -1 : table.rows.findIndex((r) => r.id === id));
  /** The cell `delta` steps on in reading order; past the last cell a new row, with `grow`. */
  const step = (pos: Pos, delta: 1 | -1, grow: boolean): Pos | null => {
    const width = table.columns.length;
    const flat = (rowIndex(pos.row) + 1) * width + colIndex(pos.col) + delta;
    if (flat < 0) return null;
    const r = Math.floor(flat / width) - 1;
    const c = flat % width;
    if (r >= table.rows.length) {
      if (!grow || readOnly) return null;
      return { row: addRow(handle.doc, body, table.rows.length), col: table.columns[0]!.id };
    }
    return { row: r < 0 ? null : table.rows[r]!.id, col: table.columns[c]!.id };
  };
  const down = (pos: Pos): Pos | null => {
    const r = rowIndex(pos.row) + 1;
    if (r < table.rows.length) return { row: table.rows[r]!.id, col: pos.col };
    if (readOnly) return null;
    return { row: addRow(handle.doc, body, table.rows.length), col: pos.col };
  };
  const actions: CellActions = {
    next: (pos, delta) => setActive(step(pos, delta, delta === 1)),
    down: (pos) => setActive(down(pos)),
    leave: () => setActive(null),
    moveRow: (pos, delta) => !!pos.row && moveRow(handle.doc, body, pos.row, delta),
    moveColumn: (pos, delta) => moveColumn(handle.doc, body, pos.col, delta),
    paste: (pos, text) => {
      const gfm = parseGfm(text);
      if (gfm) {
        // a whole table pasted into the header row replaces the headers too
        const grid = pos.row === null ? [gfm.headers, ...gfm.rows] : gfm.rows;
        pasteGrid(handle.doc, body, pos.row, pos.col, grid, gfm.aligns);
        return true;
      }
      const tsv = parseTsv(text);
      if (!tsv) return false;
      pasteGrid(handle.doc, body, pos.row, pos.col, tsv);
      return true;
    },
  };

  const copy = () => {
    void navigator.clipboard.writeText(toGfm(table)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <div className="px-1 py-1" data-testid="table-block" onFocus={onFocus}>
      {(!readOnly || table.caption) && (
        <LoroTextInput handle={handle} map={body} field="caption" placeholder={t("table.caption")} readOnly={readOnly} testId="table-caption" className="mb-1 w-full bg-transparent text-sm font-medium text-neutral-700 outline-none placeholder:text-neutral-400 dark:text-neutral-200" />
      )}
      {!readOnly && <Toolbar handle={handle} body={body} table={table} active={active} setActive={setActive} copied={copied} onCopy={copy} />}
      {readOnly && (
        <div className="mb-1 flex justify-end">
          <button className="flex items-center gap-1 text-xs text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200" data-testid="table-copy" onClick={copy}>
            <ClipboardCopy size={13} /> {copied ? t("table.copied") : t("table.copy")}
          </button>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm" data-testid="table-grid">
          <thead>
            <tr>
              {table.columns.map((c) => (
                <Cell key={c.id} handle={handle} body={body} pos={{ row: null, col: c.id }} value={c.header} align={c.align} header readOnly={readOnly} active={samePos(active, { row: null, col: c.id })} onActivate={setActive} actions={actions} />
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((r) => (
              <tr key={r.id} data-testid="table-row" data-row-id={r.id}>
                {table.columns.map((c) => (
                  <Cell key={c.id} handle={handle} body={body} pos={{ row: r.id, col: c.id }} value={r.cells[c.id] ?? ""} align={c.align} readOnly={readOnly} active={samePos(active, { row: r.id, col: c.id })} onActivate={setActive} actions={actions} />
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface CellActions {
  next(pos: Pos, delta: 1 | -1): void;
  down(pos: Pos): void;
  leave(): void;
  moveRow(pos: Pos, delta: number): boolean;
  moveColumn(pos: Pos, delta: number): boolean;
  paste(pos: Pos, text: string): boolean;
}

function Cell({ handle, body, pos, value, align, header, readOnly, active, onActivate, actions }: { handle: DocHandle; body: LoroMap; pos: Pos; value: string; align: Align; header?: boolean; readOnly: boolean; active: boolean; onActivate(pos: Pos): void; actions: CellActions }) {
  const Tag = header ? "th" : "td";
  const cls =
    "border border-neutral-200 px-2 py-1 align-top dark:border-neutral-800 " +
    (header ? "bg-neutral-100 font-semibold dark:bg-neutral-900 " : "") +
    (align === "center" ? "text-center" : align === "right" ? "text-right" : "text-left");
  return (
    <Tag className={cls} data-testid={header ? "table-header" : "table-cell"} data-col-id={pos.col} data-active={active || undefined} onClick={() => !readOnly && !active && onActivate(pos)}>
      {active && !readOnly ? (
        <CellEditor handle={handle} body={body} pos={pos} actions={actions} />
      ) : (
        // inline Markdown with raw HTML off: markdown-it escapes it and refuses javascript: links
        <span className="ProseMirror-rendered block min-h-[1.25rem]" dangerouslySetInnerHTML={{ __html: inline.renderInline(value) }} />
      )}
    </Tag>
  );
}

function CellEditor({ handle, body, pos, actions }: { handle: DocHandle; body: LoroMap; pos: Pos; actions: CellActions }) {
  const mountRef = useRef<HTMLDivElement>(null);
  const latest = useRef({ pos, actions });
  useLayoutEffect(() => {
    latest.current = { pos, actions };
  });
  useEffect(() => {
    // resolved here, once per cell: a handle is a new object on every read
    const text = pos.row === null ? headerText(body, pos.col) : cellText(body, pos.row, pos.col);
    if (!mountRef.current || !text) return;
    const run = (fn: () => unknown) => () => {
      fn();
      return true;
    };
    const view = new EditorView({
      parent: mountRef.current,
      state: EditorState.create({
        doc: text.toString(),
        extensions: [
          loroTextBinding(handle.doc, text),
          EditorView.lineWrapping,
          // one line per cell: pasted line breaks become spaces (never rewrite a change from Loro)
          EditorState.transactionFilter.of((tr) => {
            if (!tr.docChanged || tr.annotation(fromLoro) || !tr.newDoc.toString().includes("\n")) return tr;
            const changes: { from: number; to: number; insert: string }[] = [];
            tr.changes.iterChanges((fromA, toA, _fb, _tb, inserted) => changes.push({ from: fromA, to: toA, insert: inserted.toString().replace(/\r?\n/g, " ") }));
            return { changes, selection: tr.selection };
          }),
          EditorView.domEventHandlers({
            paste: (event) => {
              const pasted = event.clipboardData?.getData("text/plain") ?? "";
              if (!pasted.includes("\n") && !pasted.includes("\t")) return false;
              if (!latest.current.actions.paste(latest.current.pos, pasted)) return false;
              event.preventDefault();
              return true;
            },
          }),
          Prec.high(
            keymap.of([
              { key: "Tab", run: run(() => latest.current.actions.next(latest.current.pos, 1)) },
              { key: "Shift-Tab", run: run(() => latest.current.actions.next(latest.current.pos, -1)) },
              { key: "Enter", run: run(() => latest.current.actions.down(latest.current.pos)) },
              { key: "Escape", run: run(() => latest.current.actions.leave()) },
              { key: "Alt-ArrowUp", run: () => latest.current.actions.moveRow(latest.current.pos, -1) },
              { key: "Alt-ArrowDown", run: () => latest.current.actions.moveRow(latest.current.pos, 1) },
              { key: "Alt-ArrowLeft", run: () => latest.current.actions.moveColumn(latest.current.pos, -1) },
              { key: "Alt-ArrowRight", run: () => latest.current.actions.moveColumn(latest.current.pos, 1) },
            ]),
          ),
        ],
      }),
    });
    view.focus();
    view.dispatch({ selection: { anchor: view.state.doc.length } });
    return () => view.destroy();
    // one editor per activated cell; position and actions are read through `latest`
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pos.row, pos.col]);
  return <div ref={mountRef} className="min-w-[4rem] [&_.cm-editor]:bg-transparent [&_.cm-focused]:outline-none" data-testid="table-cell-editor" />;
}

function Toolbar({ handle, body, table, active, setActive, copied, onCopy }: { handle: DocHandle; body: LoroMap; table: TableState; active: Pos | null; setActive(p: Pos | null): void; copied: boolean; onCopy(): void }) {
  const t = useT();
  const at = active ? { row: active.row === null ? -1 : table.rows.findIndex((r) => r.id === active.row), col: table.columns.findIndex((c) => c.id === active.col) } : null;
  const align = active ? table.columns.find((c) => c.id === active.col)?.align ?? null : null;
  const button = (label: string, testId: string, icon: ReactNode, onClick: () => void, disabled = false) => (
    <button
      className="rounded-sm p-1 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800 disabled:opacity-40 dark:hover:bg-neutral-800 dark:hover:text-neutral-200"
      title={label}
      aria-label={label}
      data-testid={testId}
      disabled={disabled}
      // keep the cell's editor focused while a toolbar button is pressed
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
    >
      {icon}
    </button>
  );
  const rowAt = (offset: number) => {
    const index = (at ? Math.max(at.row, 0) + offset : table.rows.length);
    const id = addRow(handle.doc, body, index);
    setActive({ row: id, col: active?.col ?? table.columns[0]!.id });
  };
  const colAt = (offset: number) => {
    const index = at ? at.col + offset : table.columns.length;
    const id = addColumn(handle.doc, body, index);
    setActive({ row: active?.row ?? null, col: id });
  };
  return (
    <div className="mb-1 flex flex-wrap items-center gap-0.5" data-testid="table-toolbar">
      {button(t("table.rowAbove"), "table-row-above", <ArrowUpToLine size={14} />, () => rowAt(0), !at || at.row < 0)}
      {button(t("table.rowBelow"), "table-row-below", <ArrowDownToLine size={14} />, () => rowAt(at && at.row >= 0 ? 1 : 0))}
      {button(t("table.colLeft"), "table-col-left", <ArrowLeftToLine size={14} />, () => colAt(0), !at)}
      {button(t("table.colRight"), "table-col-right", <ArrowRightToLine size={14} />, () => colAt(1))}
      <span className="mx-1 h-4 w-px bg-neutral-200 dark:bg-neutral-800" />
      {button(t("table.deleteRow"), "table-delete-row", <Rows2 size={14} />, () => {
        if (active?.row) deleteRow(handle.doc, body, active.row);
        setActive(null);
      }, !active?.row)}
      {button(t("table.deleteColumn"), "table-delete-column", <Columns2 size={14} />, () => {
        if (active) deleteColumn(handle.doc, body, active.col);
        setActive(null);
      }, !active || table.columns.length < 2)}
      <span className="mx-1 h-4 w-px bg-neutral-200 dark:bg-neutral-800" />
      {(["left", "center", "right"] as const).map((a) =>
        button(t(ALIGN_LABEL[a]), `table-align-${a}`, a === "left" ? <AlignLeft size={14} /> : a === "center" ? <AlignCenter size={14} /> : <AlignRight size={14} />, () => active && setAlign(handle.doc, body, active.col, align === a ? null : a), !active),
      )}
      <span className="grow" />
      <button className="flex items-center gap-1 text-xs text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200" data-testid="table-copy" onMouseDown={(e) => e.preventDefault()} onClick={onCopy}>
        <ClipboardCopy size={13} /> {copied ? t("table.copied") : t("table.copy")}
      </button>
    </div>
  );
}
